<div>
  <Navbar />
  <Sidebar />

  <div class="page-wrapper">
    <div class="hero-section w-100">
      <div class="border rounded-4 p-2 custom-border3..">
        <!-- Header -->
        <div class="mb-4">
          <h2 class="fw-bold gradient-text">Superset Admin</h2>
          <p class="text-muted">Manage and sync users with Superset.</p>
        </div>

        <!-- Tabs -->
        <ul class="nav nav-tabs nav-justified mb-3">
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: active_tab === 'sync' }"
              type="button"
              @click="switch_tab('sync')"
            >
              <i class="bi bi-people me-2"></i>
              User Sync Management
            </button>
          </li>

          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: active_tab === 'dashboards' }"
              type="button"
              @click="switch_tab('dashboards')"
            >
              <i class="bi bi-grid me-2"></i>
              Dashboard Creation
            </button>
          </li>
        </ul>

        <!-- TAB: USER SYNC MANAGEMENT -->
        <div v-if="active_tab === 'sync'" class="p-3 p-md-4">
          <!-- Summary cards -->
          <div class="row text-center mb-3">
            <div class="col-md-4 mb-3">
              <div class="card rounded-4 shadow-sm">
                <div class="card-body">
                  <p class="card-text display-6 mb-0">{{ aic_users.length }}</p>
                  <h6 class="card-title">Total Users</h6>
                </div>
              </div>
            </div>

            <div class="col-md-4 mb-3">
              <div class="card rounded-4 shadow-sm">
                <div class="card-body">
                  <p class="card-text display-6 mb-0">{{ synced_users.length }}</p>
                  <h6 class="card-title">Synced</h6>
                </div>
              </div>
            </div>

            <div class="col-md-4 mb-3">
              <div class="card rounded-4 shadow-sm">
                <div class="card-body">
                  <p class="card-text display-6 mb-0">{{ not_synced_users.length }}</p>
                  <h6 class="card-title">Not Synced</h6>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3">
            <div class="d-flex gap-2">
              <button
                class="btn upload-button d-flex align-items-center gap-2"
                @click="sync_all_open_modal"
                :disabled="loading_any || !not_synced_users.length"
              >
                <i class="bi bi-arrow-repeat"></i>
                Sync All Users
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="card table-card">
            <div class="card-body">
              <div class="table-responsive content-table">
                <table class="table text-xs table-striped" id="superset-users-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Sync Status</th>
                      <th>Last Synced</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody class="text-start text-xs">
                    <tr v-if="loading_any">
                      <td colspan="6" class="text-center py-4">
                        <div class="spinner-border" role="status"></div>
                        <div class="text-muted mt-2">Loading...</div>
                      </td>
                    </tr>

                    <tr v-else-if="!aic_users.length">
                      <td colspan="6" class="text-center text-muted py-4">
                        No users found.
                      </td>
                    </tr>

                    <tr v-else v-for="(u, index) in aic_users" :key="u.id">
                      <td>{{ index + 1 }}</td>
                      <td class="fw-semibold">{{ display_name(u) }}</td>
                      <td class="text-muted">{{ u.email }}</td>

                      <td>
                        <span>
                          {{ u.role || "—" }}
                        </span>
                      </td>

                      <td>
                        <span v-if="is_synced(u.email)" class="text-success d-flex align-items-center gap-2">
                          <i class="bi bi-check-circle-fill"></i>
                          Synced
                        </span>
                        <span v-else class="text-danger d-flex align-items-center gap-2">
                          <i class="bi bi-x-circle-fill"></i>
                          Not Synced
                        </span>
                      </td>

                      <td class="text-muted">
                        {{ is_synced(u.email) ? "—" : "Never" }}
                      </td>

                      <td>
                        <button
                          v-if="!is_synced(u.email)"
                          class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
                          @click="open_sync_modal(u)"
                        >
                          Sync Now
                        </button>

                        <span v-else class="text-muted small">Up to date</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- TAB: DASHBOARD CREATION -->
        <div v-else class="p-3 p-md-4">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3">
            <div>
              <h4 class="mb-1">Dashboard Creation</h4>
              <p class="text-muted mb-0">Create a dashboard in Superset and register it in AIC One.</p>
            </div>

            <button
              class="btn btn-primary d-flex align-items-center gap-2"
              data-bs-toggle="modal"
              data-bs-target="#createDashboardModal"
            >
              <i class="bi bi-plus-circle"></i>
              Create Dashboard
            </button>
          </div>

          <div class="card rounded-4 shadow-sm">
            <div class="card-body">
              <p class="text-muted mb-0">
                Your backend exposes dashboard creation via:
                <code>admin_create_superset_dashboard_api</code>.
                This page uses that API via the modal below.
              </p>
            </div>
          </div>
        </div>

        <!-- Sync User Modal -->
        <div class="modal fade" id="sync_user_modal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title gradient-text">Sync User to Superset</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div class="modal-body">
                <div class="mb-4">
                  <div class="fw-semibold">{{ display_name(selected_user) }}</div>
                  <div class="text-muted small">{{ selected_user?.email }}</div>
                </div>

                <div class="mb-3 text-start">
                  <label class="form-label d-block position-static mb-2">Superset Password</label>
                  <input
                    v-model="sync_form.superset_password"
                    type="password"
                    class="form-control"
                    placeholder="Enter a password for Superset"
                    autocomplete="new-password"
                  />
                </div>

                <div class="mb-3 text-start">
                  <label class="form-label d-block position-static mb-2">Superset Roles</label>
                  <select v-model="sync_form.superset_roles" class="form-select" multiple>
                    <option v-for="r in superset_roles" :key="r.id" :value="r.name">
                      {{ r.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="modal-footer">

                <button
                  class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
                  @click="sync_selected_user"
                  :disabled="sync_user_loading"
                >
                  <span
                    v-if="sync_user_loading"
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Sync Now
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sync All Modal -->
        <div class="modal fade" id="syncAllModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content rounded-4">
              <div class="modal-header">
                <h5 class="modal-title gradient-text">Sync All Users</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div class="modal-body">
                <p class="mb-3 text-muted small">
                  This will sync <strong>{{ not_synced_users.length }}</strong> unsynced users.
                </p>

                <div class="mb-3 text-start">
                  <label class="form-label d-block position-static mb-2">Default Superset Password</label>
                  <input
                    v-model="sync_all_form.superset_password"
                    type="password"
                    class="form-control"
                    placeholder="Enter one password to apply to all"
                    autocomplete="new-password"
                  />
                </div>

                <div class="mb-3 text-start">
                  <label class="form-label d-block position-static mb-2">Default Superset Roles</label>
                  <select v-model="sync_all_form.superset_roles" class="form-select" multiple>
                    <option v-for="r in superset_roles" :key="r.id" :value="r.name">
                      {{ r.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="modal-footer">

                <button
                  class="upload-button btn-sm text-white box-shadow small d-flex align-items-center"
                  @click="sync_all_users"
                  :disabled="sync_all_loading"
                >
                  <span
                    v-if="sync_all_loading"
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Sync All
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Create Dashboard Modal -->
        <div class="modal fade" id="createDashboardModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content rounded-4">
              <div class="modal-header">
                <h5 class="modal-title fw-semibold">Create Superset Dashboard</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div class="modal-body">
                <div class="mb-3 text-start">
                  <label class="form-label d-block position-static mb-2">Dashboard Name</label>
                  <input
                    v-model="dashboard_form.dashboard_name"
                    type="text"
                    class="form-control"
                    placeholder="e.g. Team KPIs"
                    required
                  />
                </div>

                <div class="mb-3 text-start">
                  <label class="form-label d-block position-static mb-2">Creator (User)</label>
                  <select v-model="dashboard_form.user_id" class="form-select">
                    <option value="" disabled>Select user</option>
                    <option v-for="u in aic_users" :key="u.id" :value="u.id">
                      {{ display_name(u) }} ({{ u.email }})
                    </option>
                  </select>
                </div>
              </div>

              <div class="modal-footer">

                <button
                  class="btn btn-primary d-flex align-items-center gap-2"
                  @click="create_dashboard"
                  :disabled="create_dashboard_loading"
                >
                  <span
                    v-if="create_dashboard_loading"
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- /border -->
      </div>
      <!-- /hero-section -->
    </div>
    <!-- /page-wrapper -->
  </div>
</div>
